import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addMetadataElementsToCanvas } from '../../reducers/united-canvas/unitedCanvasReducer';
import { selectDefaultCreationCrmOrgId } from '../../reducers/userInfoReducer';
import { FunnelType } from '../../types/enums/FunnelType';

/**
 * Hook for adding healthcare-specific metadata elements to the canvas
 * These elements are predefined for the Dreamforce demo healthcare scenario
 */
export const useAddHealthcareMetadataElements = () => {
  const dispatch = useDispatch();
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);

  const addHealthcareMetadataElements = useCallback(() => {
    if (!crmOrgId) {
      console.warn('No CRM Org ID available for adding healthcare metadata elements');
      return;
    }

    console.log('🏥 Adding healthcare metadata elements to canvas');

    // Healthcare metadata elements for the demo
    const healthcareMetadataElements = [
      {
        name: 'Appointment Date',
        type: 'Date',
        crmOrgId: crmOrgId,
        metadataId: 'salesforce.AppointmentScheduleLog.field.AppointmentDate',
        position: { row: -6, column: 0 },
        color: '#FFF4BF',
        icon: 'field_sales'
      },
      {
        name: 'Checkup Reminder',
        type: 'Flow',
        crmOrgId: crmOrgId,
        metadataId: 'salesforce.Flow.instance.Count_Days_Since_Last_Activity',
        position: { row: -3, column: 4 },
        color: '#DEFBFF',
        icon: 'flow'
      },
      {
        name: 'New Lead Assignment',
        type: 'Flow',
        crmOrgId: crmOrgId,
        metadataId: 'salesforce.Flow.instance.New_Lead',
        position: { row: -2, column: 0 },
        color: '#DEFBFF',
        icon: 'flow'
      },
      {
        name: 'Assigned Dentist',
        type: 'Email',
        crmOrgId: crmOrgId,
        metadataId: 'salesforce.Lead.field.Assigned_Dentist__c',
        position: { row: -3, column: 1 },
        color: '#FFF4BF',
        icon: 'field_sales'
      },
      {
        name: 'Insurance Provider',
        type: 'Picklist',
        crmOrgId: crmOrgId,
        metadataId: 'salesforce.Lead.field.Insurance_Provider__c',
        position: { row: -4, column: 0 },
        color: '#FFF4BF',
        icon: 'field_sales'
      },
      {
        name: 'Lead Source',
        type: 'Picklist',
        crmOrgId: crmOrgId,
        metadataId: 'salesforce.Lead.field.LeadSource',
        position: { row: -7, column: 0 },
        color: '#FFF4BF',
        icon: 'field_sales'
      },
      {
        name: 'Treatment Interest',
        type: 'Picklist',
        crmOrgId: crmOrgId,
        metadataId: 'salesforce.Lead.field.Treatment_Interest__c',
        position: { row: -5, column: 0 },
        color: '#FFF4BF',
        icon: 'field_sales'
      },
      {
        name: 'Assigned Dentist',
        type: 'Email',
        crmOrgId: crmOrgId,
        metadataId: 'salesforce.Opportunity.field.Assigned_Dentist__c',
        position: { row: -7, column: 4 },
        color: '#FFF4BF',
        icon: 'field_sales'
      },
      {
        name: 'Next Appointment Date',
        type: 'Date',
        crmOrgId: crmOrgId,
        metadataId: 'salesforce.Opportunity.field.Next_Appointment_Date__c',
        position: { row: -5, column: 4 },
        color: '#FFF4BF',
        icon: 'field_sales'
      },
      {
        name: 'Treatment Plan Value',
        type: 'Currency',
        crmOrgId: crmOrgId,
        metadataId: 'salesforce.Opportunity.field.Treatment_Plan_Value__c',
        position: { row: -4, column: 4 },
        color: '#FFF4BF',
        icon: 'field_sales'
      },
      {
        name: 'Treatment Status',
        type: 'Picklist',
        crmOrgId: crmOrgId,
        metadataId: 'salesforce.Opportunity.field.Treatment_Status__c',
        position: { row: -6, column: 4 },
        color: '#FFF4BF',
        icon: 'field_sales'
      }
    ];

    // Create connections for the New Lead Assignment flow
    const connections = [
      {
        source: `${crmOrgId}_salesforce.Flow.instance.New_Lead`,
        target: `${crmOrgId}_salesforce.Lead.field.Assigned_Dentist__c`,
        targetType: FunnelType.METADATA_ELEMENT
      }
    ];

    const payload = {
      metadataElements: healthcareMetadataElements,
      connections
    };

    console.log('🏥 Dispatching addMetadataElementsToCanvas with healthcare elements:', payload);

    dispatch(addMetadataElementsToCanvas(payload));

    console.log('🏥 Healthcare metadata elements added successfully!');
  }, [dispatch, crmOrgId]);

  return { addHealthcareMetadataElements };
};
